import { useEffect, useMemo, useState } from 'react';

import { getMRTDefaultProps } from '../../../../helpers/mrt/getMRTDefaultProps';
import { useMRTState } from '../../../../hooks/useMRTState';
import useFlashMessage from '../../../../hooks/useFlashMessage';
import { podeCancelarItem, podeEditarItem, podeIncluir } from '../../../../hooks/usePermisionMRT';

import * as SubGrupoAPI from '../../../../api/subGrupo';

function criarIdentificadorLocal() {
    return `subgrupo-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function getSubGrupoRowId(row) {
    return row?.uuid || row?._localId || String(row?.id);
}

function normalizarDescricao(value) {
    return String(value ?? '').replace(/\s+/g, ' ').trim().toUpperCase();
}

function mensagemErro(error) {
    return error?.response?.data ?? {
        message: 'Ocorreu um erro ao processar o subgrupo.',
        type: 'error',
    };
}

export default function useSubGrupoModule({ dados, setDados, parametro }) {
    const [itens, setItens] = useState([]);
    const [dialog, setDialog] = useState({ open: false, selected: null });
    const [itemExcluir, setItemExcluir] = useState(null);
    const [loading, setLoading] = useState(false);

    const { setFlashMessage } = useFlashMessage();
    const persistido = Boolean(dados?.grupoId);

    const mrt = useMRTState({
        storageKey: `grupo-subgrupos-${dados?.uuid || 'novo'}`,
        initialState: {
            pagination: {
                pageIndex: 0,
                pageSize: 5,
            },
            sorting: [],
            columnFilters: [],
            columnFilterFns: {},
            globalFilter: '',
        },
    });

    const mrtDefault = useMemo(
        () =>
            getMRTDefaultProps({
                mrt,
                parametro,
            }),
        [mrt, parametro]
    );

    useEffect(() => {
        const subGrupos = dados?.subGrupos ?? dados?.subGrupo ?? [];
        setItens(Array.isArray(subGrupos) ? subGrupos : []);
    }, [dados?.grupoId, dados?.subGrupo, dados?.subGrupos]);

    const sincronizar = (subGrupos) => {
        setItens(subGrupos);
        setDados((prev) => ({
            ...prev,
            subGrupos,
        }));
    };

    const abrirNovo = () => {
        setDialog({ open: true, selected: null });
    };

    const abrirEdicao = (selected) => {
        setDialog({ open: true, selected });
    };

    const fecharDialog = () => {
        if (loading) return;
        setDialog({ open: false, selected: null });
    };

    const confirmar = async (subGrupo) => {
        const descricao = normalizarDescricao(subGrupo?.descricao);
        const selectedId = dialog.selected ? getSubGrupoRowId(dialog.selected) : null;

        const duplicado = itens.some(
            (item) => normalizarDescricao(item.descricao) === descricao && getSubGrupoRowId(item) !== selectedId
        );

        if (duplicado) {
            setFlashMessage('Subgrupo em duplicidade!', 'warning');
            return;
        }

        if (!persistido) {
            const proximosItens = dialog.selected
                ? itens.map((item) =>
                      getSubGrupoRowId(item) === selectedId
                          ? {
                                ...item,
                                ...subGrupo,
                                descricao,
                            }
                          : item
                  )
                : [
                      ...itens,
                      {
                          ...subGrupo,
                          descricao,
                          grupoId: null,
                          _localId: criarIdentificadorLocal(),
                      },
                  ];

            sincronizar(proximosItens);
            setDialog({ open: false, selected: null });
            return;
        }

        setLoading(true);

        try {
            const response = dialog.selected
                ? await SubGrupoAPI.editar(dados.grupoId, dialog.selected.uuid, {
                      descricao,
                      ativo: subGrupo.ativo,
                  })
                : await SubGrupoAPI.cadastrar(dados.grupoId, {
                      descricao,
                      ativo: subGrupo.ativo,
                  });

            const salvo = response.data.data;
            const proximosItens = dialog.selected
                ? itens.map((item) => (getSubGrupoRowId(item) === selectedId ? salvo : item))
                : [...itens, salvo];

            sincronizar(proximosItens);
            setFlashMessage(response.data.message, response.data.type);
            setDialog({ open: false, selected: null });
        } catch (error) {
            const retorno = mensagemErro(error);
            setFlashMessage(retorno.message, retorno.type);
        } finally {
            setLoading(false);
        }
    };

    const solicitarExclusao = (item) => {
        setItemExcluir(item);
    };

    const cancelarExclusao = () => {
        if (loading) return;
        setItemExcluir(null);
    };

    const confirmarExclusao = async () => {
        if (!itemExcluir) return;

        const rowId = getSubGrupoRowId(itemExcluir);

        if (!persistido) {
            sincronizar(itens.filter((item) => getSubGrupoRowId(item) !== rowId));
            setItemExcluir(null);
            return;
        }

        setLoading(true);

        try {
            const response = await SubGrupoAPI.excluir(dados.grupoId, itemExcluir.uuid);

            sincronizar(itens.filter((item) => getSubGrupoRowId(item) !== rowId));
            setFlashMessage(response.data.message, response.data.type);
            setItemExcluir(null);
        } catch (error) {
            const retorno = mensagemErro(error);
            setFlashMessage(retorno.message, retorno.type);
        } finally {
            setLoading(false);
        }
    };

    return {
        itens,
        getSubGrupoRowId,
        mrtDefault,
        dialog,
        itemExcluir,
        loading,
        abrirNovo,
        abrirEdicao,
        fecharDialog,
        confirmar,
        solicitarExclusao,
        cancelarExclusao,
        confirmarExclusao,
        podeIncluir: podeIncluir(),
        podeEditar: podeEditarItem(),
        podeExcluir: podeCancelarItem(),
    };
}
